import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import {logCheck, logout} from "@/Api/login";
import {
  loginCheck,
  loginRefresh,
  loginRemove,
  loginStore,
} from "@/fonction/logConnection";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {testConnection} from "@/hooks/useIsConnectedTest/useIsConnectedTest";
import {AppContext as AppContextType} from "@/ts/appContext";

import {AppContext} from "./AppContext";

export type LogContextType = {
  isConnected: boolean;
  setIsConnected: (isConnected: boolean) => void;
  handleLogin: () => void;
  handleLogout: () => void;
  droits:
    | {
        [key: string]: boolean;
      }
    | undefined;
};

/**
 * Contexte global pour gérer les paramètres de l'application
 * Notamment l'authentification et les droits utilisateur
 */
export const LogContext = createContext<LogContextType | null>({
  isConnected: false,
  setIsConnected: () => {},
  handleLogin: () => {},
  handleLogout: () => {},
  droits: undefined,
});

export function LogContextProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const navigate = useNavigate();
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  // Temps d'expiration du token (12 heures)
  const expiryTime = 43200000;
  loginStore(expiryTime);
  const [searchParams] = useSearchParams();
  // État de connexion de l'utilisateur
  const [isConnected, setIsConnected] = useState(loginCheck());
  const [droits, setDroits] = useState<
    | {
        [key: string]: boolean;
      }
    | undefined
  >(undefined);

  /**
   * Vérifie la validité de la connexion utilisateur
   * Met à jour l'état de connexion et les droits utilisateur
   */
  const loginCheckHandler = async (): Promise<void> => {
    if (loginCheck()) {
      try {
        const {droits} = await logCheck(API);

        setDroits(droits);
        loginRefresh(expiryTime);
        setIsConnected(true);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_) {
        // En cas d'erreur, déconnexion de l'utilisateur
        loginRemove();
        setIsConnected(false);
      }
    }
  };

  /**
   * Effet pour vérifier périodiquement la validité de la connexion (toutes les minutes)
   */
  useEffect(() => {
    testConnection(isConnected, navigate);
    // Suppression du paramètre isConnected de l'URL
    if (searchParams.get("isConnected")) {
      searchParams.delete("isConnected");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${searchParams.toString()}`
      );
    }
    const intervalLogCheck = setInterval(async () => {
      await loginCheckHandler();
    }, 60000); // Vérification toutes les 60 secondes

    // Nettoyage de l'intervalle lors du démontage du composant
    return () => {
      clearInterval(intervalLogCheck);
    };
  }, [searchParams, isConnected]);

  /**
   * Effet pour vérifier la connexion lors du changement des paramètres d'URL
   * ou de l'état de connexion
   */
  useEffect(() => {
    loginCheckHandler();
    // Suppression du paramètre isConnected de l'URL
  }, []);

  /**
   * Action de connexion de l'utilisateur (Clique bouton connexion)
   */
  const handleLogin = useCallback(() => {
    const url = `${API}/login/?url=${encodeURIComponent(
      window.location.origin
    )}/`;

    window.location.href = url;
  }, [API]);

  /**
   * Action de déconnexion de l'utilisateur (Clique bouton déconnexion)
   */
  const handleLogout = async (): Promise<void> => {
    try {
      await logout(API);
      setIsConnected(false);
      navigate("/connexion");
    } catch (error: any) {
      alert(`Erreur lors de la déconnexion: ${error?.message}`);
    }
  };
  /**
   * Rendu du Provider avec les valeurs du contexte
   */
  return (
    <LogContext.Provider
      value={{
        isConnected, // État de connexion
        setIsConnected, // Fonction pour modifier l'état de connexion
        handleLogin,
        handleLogout,
        droits,
      }}>
      {children}
    </LogContext.Provider>
  );
}
